/**
 * Template de prise de besoin : Plan d'intervention en cas d'incident (IRP) v1
 * Aligné sur NIST CSF 2.0, ITIL 4, ISO 27001 et COBIT 2019
 * Plan détaillé d'intervention pour répondre aux incidents de sécurité
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePlanInterventionIncidentV1: IntakeTemplate = {
  id: 'plan-intervention-incident-v1',
  name: 'Prise de besoin Plan d\'intervention en cas d\'incident (IRP) v1',
  description:
    'Questionnaire complet pour élaborer un plan d\'intervention en cas d\'incident conforme NIST CSF 2.0, ITIL 4, ISO 27001 et COBIT 2019. Définit les procédures, rôles, outils et workflows pour la détection, analyse, confinement, éradication et restauration.',
  documentTypeCode: 'INCIDENT_RESPONSE_PLAN',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Contexte & Équipe d\'Intervention',
      description: 'Informations sur l\'entreprise et structure de l\'équipe d\'intervention',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour le plan d\'intervention.',
        },
        {
          id: 'equipe_intervention',
          label: 'Qui compose l\'équipe d\'intervention en cas d\'incident ?',
          type: 'multiselect',
          options: [
            'Responsable sécurité',
            'Gestionnaire d\'incidents',
            'Techniciens sécurité',
            'Administrateurs systèmes',
            'Administrateurs réseau',
            'Experts forensiques',
            'Direction',
            'Communication / Relations publiques',
            'Juridique',
            'Autre',
          ],
          required: true,
          helpText: 'NIST CSF 2.0 (RS.CO): Équipe d\'intervention essentielle pour réponse efficace.',
        },
        {
          id: 'responsable_equipe',
          label: 'Qui est le responsable de l\'équipe d\'intervention ?',
          type: 'text',
          placeholder: 'Ex: Responsable Cybersécurité, Directeur TI...',
          required: true,
          helpText: 'ITIL 4: Attribution claire du responsable pour coordination.',
        },
        {
          id: 'contact_urgence',
          label: 'Quels sont les contacts d\'urgence (24/7) ?',
          type: 'textarea',
          placeholder: 'Listez les contacts d\'urgence avec numéros de téléphone, courriels, etc.',
          helpText: 'NIST CSF 2.0 (RS.CO): Contacts d\'urgence essentiels pour réponse rapide.',
        },
        {
          id: 'ton_document_plan',
          label: 'Quel ton souhaitez-vous pour ce plan ?',
          type: 'select',
          options: [
            'Formel et procédural (ton impératif, étapes claires)',
            'Pédagogique et explicatif (ton guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la facilité d\'utilisation du plan en situation d\'urgence.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Détection & Analyse',
      description: 'Procédures de détection et d\'analyse des incidents',
      icon: 'Eye',
      questions: [
        {
          id: 'sources_detection',
          label: 'Quelles sont les sources de détection d\'incidents ?',
          type: 'multiselect',
          options: [
            'Monitoring SIEM',
            'Alertes antivirus',
            'Alertes firewall',
            'Alertes IDS/IPS',
            'Rapports utilisateurs',
            'Alertes cloud (Microsoft 365, AWS, etc.)',
            'Surveillance réseau',
            'Audits de sécurité',
            'Autre',
          ],
          required: true,
          helpText: 'NIST CSF 2.0 (DE.CM): Sources de détection pour identification rapide.',
        },
        {
          id: 'processus_detection',
          label: 'Quel est le processus de détection et d\'analyse ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: réception alerte → triage → analyse initiale → classification...',
          helpText: 'NIST CSF 2.0 (RS.AN): Processus structuré pour analyse efficace.',
        },
        {
          id: 'outils_analyse',
          label: 'Quels outils sont utilisés pour l\'analyse ?',
          type: 'multiselect',
          options: [
            'SIEM (Security Information and Event Management)',
            'Outils forensiques',
            'Analyseurs de logs',
            'Outils de réseau',
            'Outils de malware analysis',
            'Outils cloud',
            'Autre',
          ],
          helpText: 'NIST CSF 2.0 (RS.AN): Outils appropriés pour analyse approfondie.',
        },
        {
          id: 'criteres_classification',
          label: 'Quels critères sont utilisés pour classifier la sévérité ?',
          type: 'multiselect',
          options: [
            'Impact business',
            'Nombre d\'utilisateurs affectés',
            'Criticité des systèmes',
            'Type de menace',
            'Données exposées',
            'Conformité réglementaire',
            'Autre',
          ],
          helpText: 'ITIL 4: Critères de classification pour priorisation.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Confinement & Éradication',
      description: 'Procédures de confinement et d\'éradication',
      icon: 'Shield',
      questions: [
        {
          id: 'mesures_confinement',
          label: 'Quelles mesures de confinement sont prévues ?',
          type: 'multiselect',
          options: [
            'Isolation réseau',
            'Déconnexion de systèmes',
            'Blocage d\'accès',
            'Désactivation de comptes',
            'Modification de règles firewall',
            'Mise en quarantaine',
            'Autre',
          ],
          required: true,
          helpText: 'NIST CSF 2.0 (RS.MI): Mesures de confinement pour limiter l\'impact.',
        },
        {
          id: 'processus_confinement',
          label: 'Quel est le processus de confinement ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: évaluation → décision → mise en œuvre → vérification...',
          helpText: 'NIST CSF 2.0 (RS.MI): Processus structuré pour confinement efficace.',
        },
        {
          id: 'mesures_eradication',
          label: 'Quelles mesures d\'éradication sont prévues ?',
          type: 'multiselect',
          options: [
            'Suppression de malware',
            'Correction de vulnérabilités',
            'Réinitialisation de systèmes',
            'Nettoyage de données',
            'Mise à jour de sécurité',
            'Modification de configurations',
            'Autre',
          ],
          required: true,
          helpText: 'NIST CSF 2.0 (RS.MI): Mesures d\'éradication pour éliminer la menace.',
        },
        {
          id: 'processus_eradication',
          label: 'Quel est le processus d\'éradication ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: identification → planification → exécution → vérification...',
          helpText: 'NIST CSF 2.0 (RS.MI): Processus structuré pour éradication complète.',
        },
        {
          id: 'verification_eradication',
          label: 'Comment vérifie-t-on que l\'éradication est complète ?',
          type: 'textarea',
          placeholder: 'Décrivez les méthodes de vérification: scans, tests, audits...',
          helpText: 'NIST CSF 2.0 (RS.MI): Vérification essentielle pour confirmer l\'éradication.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Restauration & Post-Incident',
      description: 'Procédures de restauration et activités post-incident',
      icon: 'CheckCircle',
      questions: [
        {
          id: 'strategie_restauration',
          label: 'Quelle est la stratégie de restauration ?',
          type: 'select',
          options: [
            'Restauration depuis sauvegardes',
            'Reconstruction complète',
            'Restauration progressive',
            'Basculement vers site de secours',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4 (ITSCM): Stratégie de restauration pour rétablir les services.',
        },
        {
          id: 'processus_restauration',
          label: 'Quel est le processus de restauration ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: préparation → restauration → tests → validation → remise en service...',
          helpText: 'NIST CSF 2.0 (RS.IM): Processus structuré pour restauration efficace.',
        },
        {
          id: 'delais_restauration',
          label: 'Quels sont les délais cibles de restauration (RTO) ?',
          type: 'textarea',
          placeholder: 'Ex: Services critiques: 4 heures, Services standards: 24 heures...',
          helpText: 'ITIL 4 (ITSCM): Délais cibles pour restauration (RTO - Recovery Time Objective).',
        },
        {
          id: 'activites_post_incident',
          label: 'Quelles activités post-incident sont prévues ?',
          type: 'multiselect',
          options: [
            'Rapport d\'incident',
            'Analyse de cause racine',
            'Leçons apprises',
            'Amélioration des procédures',
            'Formation supplémentaire',
            'Mise à jour du plan',
            'Autre',
          ],
          helpText: 'ITIL 4: Activités post-incident pour amélioration continue.',
        },
        {
          id: 'revision_plan',
          label: 'À quelle fréquence souhaitez-vous réviser ce plan ?',
          type: 'select',
          options: ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Après chaque incident majeur', 'À déterminer'],
          helpText: 'NIST CSF 2.0: Révision régulière essentielle pour maintenir l\'efficacité.',
        },
      ],
    },
  ],
};





